<template>
  <form
    class="checkout-login__register-form"
    @submit.prevent="form.handleSubmit"
  >
    <div class="checkout-login__form-grid">
      <TanStackFormInput
        :form="form"
        name="email"
        :label="t('checkout.personalDetails.form.email')"
        type="email"
        autocomplete="email"
        :disabled="isLoading"
        :required="true"
      />
      <TanStackFormInput
        :form="form"
        name="password"
        :label="t('checkout.personalDetails.form.password')"
        type="password"
        autocomplete="new-password"
        :disabled="isLoading"
        :required="true"
      />
      <TanStackFormInput
        :form="form"
        name="first_name"
        :label="t('checkout.personalDetails.form.firstName')"
        type="text"
        autocomplete="last_name"
        :disabled="isLoading"
        :required="true"
      />
      <TanStackFormInput
        :form="form"
        name="last_name"
        :label="t('checkout.personalDetails.form.lastName')"
        type="text"
        autocomplete="first_name"
        :disabled="isLoading"
        :required="true"
      />
      <TanStackFormInput
        v-if="!hide.company_name"
        :form="form"
        name="company_name"
        :label="t('checkout.personalDetails.form.companyName')"
        type="text"
        autocomplete="new-company_name"
        :disabled="isLoading"
      />
      <TanStackFormInput
        v-if="!hide.phone"
        :form="form"
        name="phone"
        :label="t('checkout.personalDetails.form.phone')"
        type="tel"
        autocomplete="new-phone"
        :disabled="isLoading"
      />
    </div>
    <div class="checkout-login__form-group">
      <TanStackFormCheckbox
        :form="form"
        name="newsletter"
        :disabled="isLoading"
      >
        {{ t('checkout.newsletter') }}
      </TanStackFormCheckbox>
    </div>
    <form.Subscribe>
      <template #default="{ isSubmitting }">
        <CheckoutSubmitButton
          :is-loading="isSubmitting"
          :is-disabled="isLoading"
        >
          {{ isSubmitting ? '...' : t('checkout.continue') }}
        </CheckoutSubmitButton>
      </template>
    </form.Subscribe>
  </form>
</template>

<script setup lang="ts">
import { useForm } from '@tanstack/vue-form'
import { useI18n } from 'vue-i18n'
import { watch } from 'vue'
import CheckoutSubmitButton from '../checkout/CheckoutSubmitButton.vue'
import {
  createRegisterSchema,
  type RegisterFormData,
} from '../../../schemas/checkoutLogin'

const { t } = useI18n()

const {
  isLoading,
  email,
  hide = { company_name: false, phone: false },
} = defineProps<{
  isLoading: boolean
  email: string
  hide?: {
    company_name: boolean
    phone: boolean
  }
}>()

const emit = defineEmits<{
  (e: 'submit', data: RegisterFormData): void
  (e: 'email-change', email: string): void
}>()

const form = useForm({
  defaultValues: {
    email,
    password: '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    newsletter: false,
  },
  onSubmit: ({ value }) => {
    emit('submit', value as RegisterFormData)
  },
  validators: {
    onChange: createRegisterSchema(t),
  },
})

const emailValue = form.useStore((state) => state.values.email)

watch(emailValue, (newEmail) => {
  emit('email-change', newEmail)
})
</script>

<style lang="postcss" scoped>
.checkout-login {
  &__register-form {
    @apply space-y-4;
  }

  &__form-grid {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4;
  }

  &__form-group {
    @apply space-y-1;
  }

  &__label {
    @apply block text-sm font-medium text-gray-700;
  }

  &__input {
    @apply block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm;
  }

  &__error {
    @apply text-sm text-red;
  }

  &__checkbox {
    @apply flex items-center space-x-2;

    input[type='checkbox'] {
      @apply h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary;
    }

    span {
      @apply text-sm text-gray-700;
    }
  }

  &__submit {
    @apply w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-active focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50;
  }
}
</style>
